// Copyright (c) 2012-2021 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved. NOTICE: All information contained herein
// is, and remains the property of GRAB. The intellectual and technical concepts contained herein are confidential, proprietary
// and controlled by GRAB and may be covered by patents, patents in process, and are protected by trade secret or copyright law.
//
// You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate, adapt or change this material
// in any way unless prior written permission is obtained from GRAB. Access to the source code contained herein is hereby
// forbidden to anyone except current GRAB employees or contractors with binding Confidentiality and Non-disclosure agreements
// explicitly covering such access.
//
// The copyright notice above does not evidence any actual or intended publication or disclosure of this source code,
// which includes information that is confidential and/or proprietary, and is a trade secret, of GRAB.
//
// ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR THROUGH USE OF THIS SOURCE
// CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE LAWS AND
// INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY
// OR IMPLY ANY RIGHTS TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING
import React, {useCallback, useEffect, useRef, useState} from 'react'
import {QuestionCircleOutlined} from '@ant-design/icons'
import {useSelector, useDispatch} from 'react-redux'
import {Anchor, DatePicker, Divider, Drawer, Popover, Space, Table, Tabs, Tag, Tooltip, Typography} from 'antd'
import './job-detail.less'
import {
  BarChartOutlined,
  CameraOutlined,
  CloudUploadOutlined,
  FilterOutlined,
  ForkOutlined,
  LinkOutlined,
  RightOutlined,
  StarOutlined,
  UnorderedListOutlined,
  UserOutlined
} from '@ant-design/icons'
import {convertTimezone} from 'utils/time'
import {TaskStatusCell} from './TaskStatusCell'
import {ActionCreator} from './ActionCreator'
import moment from 'moment'
import {fetchTaskInfo, ITaskResponse} from 'api/jobs'
import {TaskStatus} from 'constants/job'
import MaskLoading from 'components/MaskLoading'
interface IStitchProps {
  taskId: string
}
const StitchDetail = (props: IStitchProps) => {
  const [taskDetail, setTaskDetail] = useState<ITaskResponse>()
  const [loading, setLoading] = useState<boolean>(true)
  const {taskId} = props
  useEffect(() => {
    setLoading(true)
    fetchTaskInfo(taskId)
      .then(res => {setTaskDetail(res)})
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [taskId])
  return (
    <div className='stitch-container'>
      {
        loading
          ?
          <MaskLoading />
          :
          <div className='stitch-info'>
            <div className="title">
              <span>Stitch status/result</span>
              <span>Count</span>
              <span>Code</span>
            </div>
            {taskDetail.results.stitchDetails?.map((item, index) => (
              <div key={index} className='item'>
                <span>{item.name}</span>
                <span>{item.count}</span>
                <span>{item.code}</span>
              </div>
            ))}
          </div>

      }
    </div>
  )
}
const JobDetail = ({visible = false, onClose, jobData: {jobName, jobID, groupID, jobDetails}, fetchTasks, actions}) => {
  const [width, setWidth] = useState(500)
  const [taskTimeRange, setTaskTimeRange] = useState([])
  const ref = useRef()
  const is360lite = groupID !== 0

  const {
    jobList: {setPaginationTasks}
  } = useDispatch()
  const {currentCity, loading, tasks, paginationTasks} = useSelector(state => ({
    ...state.jobList,
    currentCity: state.city.current,
    loading: state.loading.effects.jobList.fetchTasks
  }))
  useEffect(() => {
    let startTime: string
    let endTime: string
    if (taskTimeRange) {
      [startTime, endTime] = (taskTimeRange as any[]).map((item, index) => {
        let time: string
        if (index === 0) {
          time = convertTimezone(item.valueOf(), currentCity.timezone) + ' 00:00:00'
        } else {
          time = convertTimezone(item.valueOf(), currentCity.timezone) + ' 23: 59: 59'
        }
        return `${moment(time).unix()}`
      })
    }
    if (!jobID) return
    /**
     * ref.current  for memorizing  jobId  to detect changes ;  init  paging and other query params  when jobId changes;
     */
    if (
      ref.current &&
      ref.current !== jobID &&
      (paginationTasks.current !== 1 || paginationTasks.pageSize !== 10 || taskTimeRange.length === 2)
    ) {
      setPaginationTasks({current: 1, pageSize: 10})
      setTaskTimeRange([] as any)
      ref.current = jobID
      return
    }
    ref.current = jobID

    const query = {
      page: paginationTasks.current,
      pageSize: paginationTasks.pageSize,
      startTime,
      endTime,
      jobID
    }
    fetchTasks(query)
  }, [jobID, paginationTasks.current, paginationTasks.pageSize, taskTimeRange])
  const handleChangePage = (current, pageSize) => {
    setPaginationTasks({current, pageSize})
  }
  const handleTimeChange = useCallback(args => {
    setTaskTimeRange(args)
  }, [])
  const columns = [
    {
      title: 'Task ID',
      dataIndex: 'taskID',
    },
    {
      title: 'Capture Date',
      dataIndex: 'captureDate',
      render(text, row) {
        return convertTimezone(Number(row.startTime + '000'), currentCity.timezone, 'D/M/YYYY')
      }
    },
    {
      title: 'Capture Time',
      dataIndex: 'captureTime',
      render(text, row) {
        const startTime = convertTimezone(Number(row.startTime + '000'), currentCity.timezone, 'hh:mm A')
        const endTime = convertTimezone(Number(row.endTime + '000'), currentCity.timezone, 'hh:mm A')
        return `${startTime}-${endTime}`
      }
    },
    {
      title: () => (
        <span>
          {'Photo Uploaded'}
          <Tooltip title={'Number of photos uploaded from cameras'}>
            <QuestionCircleOutlined style={{marginLeft: 2, marginRight: 2}} />
          </Tooltip>
          {
            is360lite
            &&
            <>
              /Stitched
              < Tooltip title={'Number of stitched 360-degree photos'}>
                <QuestionCircleOutlined style={{marginLeft: 2, marginRight: 2}} />
              </Tooltip>
            </>
          }

          /PostedKV
          <Tooltip title={'Number of photos uploaded to KartaView'}>
            <QuestionCircleOutlined style={{marginLeft: 2}} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'blobImageCount',
      render(_, record) {
        const {blobImageCount, stitchedImageCount, uploadedImageCount} = record
        const showStitchDetail = is360lite && [TaskStatus.UploadingKV, TaskStatus.Stitched, TaskStatus.Stitching, TaskStatus.Successful].includes(record.status)
        console.log('record task', record)
        return (
          <Space>
            <span>
              {blobImageCount}{is360lite ? `/${stitchedImageCount}` : ''}/{uploadedImageCount}
            </span>
            {
              showStitchDetail
              &&
              <Popover content={<StitchDetail taskId={record.taskID} />} title="Stitch Details">
                <Tag color="orange" style={{cursor: 'pointer'}}>stitch details</Tag>
              </Popover>
            }
          </Space>
        )
      }
    },
    {
      title: () => (
        <span>
          {'Latest Upload'}
          <Tooltip title={'Latest upload time from KartaCam'}>
            <QuestionCircleOutlined style={{marginLeft: 2, marginRight: 2}} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'lastUploadAt',
      render(text, row) {
        const time = row.lastUploadedAt === 0 ? '---' : convertTimezone(Number(row.lastUploadedAt + '000'), currentCity.timezone, 'D/M/YYYY hh:mm A')
        return time
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render(text) {
        return <TaskStatusCell status={text} />
      }
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      render(t, row) {
        return (
          <div>
            <ActionCreator type={`task:${row.status}`} actions={actions} target={{...row, groupID}} />
          </div>
        )
      }
    }
  ]
  const title = <div className={'drawer-header'}></div>
  return (
    <Drawer
      title={title}
      className={'job-detail'}
      visible={visible}
      width={width}
      mask={true}
      // mask={false}
      onClose={onClose}
      // closeIcon={null}
      closeIcon={<RightOutlined style={{fontSize: '16px'}} />}
    >
      <div className='job-id'>
        <Typography.Title level={3}>{jobName}</Typography.Title>
      </div>
      <Tabs
        onChange={x => {
          if (x === '2') {
            // setWidth(880)
            setWidth(1000)
          } else {
            setWidth(500)
          }
        }}
      >
        <Tabs.TabPane
          tab={
            <Tooltip placement='top' title='Job Details'>
              <UnorderedListOutlined className={'title'} />
            </Tooltip>
          }
          key={1}
        >
          <div>
            <h3 className={'table-title'}>Job Details</h3>
            {Object.values(jobDetails).map(item => (
              <div key={item['label']} className={'item'}>
                <span>{item['label']}</span>
                <span>{item['value']}</span>
              </div>
            ))}
          </div>
        </Tabs.TabPane>
        <Tabs.TabPane
          tab={
            <Tooltip placement={'top'} title={'Upload Link'}>
              <LinkOutlined className={'title'} />
            </Tooltip>
          }
          key={2}
        >
          <header className={'table-title'}>
            <h3>Upload Tasks</h3>
            <DatePicker.RangePicker
              size={'small'}
              style={{width: '200px'}}
              suffixIcon={<FilterOutlined />}
              // placeholder={'  Capture date'}
              value={taskTimeRange as any}
              // value ={taskTimeRange}
              format={'  ' + 'DD/MM/YYYY'}
              onChange={handleTimeChange}
            />
          </header>
          <Table
            columns={columns}
            dataSource={tasks}
            rowKey={d => d.taskID}
            loading={loading}
            pagination={{
              onChange: handleChangePage,
              showSizeChanger: false,
              ...paginationTasks
            }}
          />
        </Tabs.TabPane>
      </Tabs>
    </Drawer>
  )
}
export {JobDetail}
